import { useMemo } from "react";
import { ACCEPTED_ONBOARDING_DATE_FORMATS } from "../../onboarding/utils/onboardingDates";
import styles from "./AssignmentDateColumnsFields.module.css";

/** Headers that identify the employee/component — never assignment dates. */
const IDENTITY_HEADER_LC = new Set([
  "employee code",
  "component name",
  "deduction name",
  "employee name",
]);

export interface AssignmentDateColumnsFieldsProps {
  headers: string[];
  startDateColumn: string;
  endDateColumn: string;
  /** Active component-name column; excluded from date options. */
  componentColumn?: string;
  /** Empty string means explicit "none"; a header name selects that column. */
  onStartDateColumnChange: (column: string) => void;
  /** Empty string means explicit "none"; a header name selects that column. */
  onEndDateColumnChange: (column: string) => void;
  disabled?: boolean;
}

/**
 * Session-level CSV column picks for assignment Start Date / End Date.
 * Mirrors the component-column selector: reserved headers are the default when
 * present; non-identity headers can be chosen for non-canonical CSVs.
 */
export function AssignmentDateColumnsFields({
  headers,
  startDateColumn,
  endDateColumn,
  componentColumn = "",
  onStartDateColumnChange,
  onEndDateColumnChange,
  disabled = false,
}: AssignmentDateColumnsFieldsProps) {
  const dateHeaders = useMemo(
    () =>
      headers.filter((header) => {
        const lc = header.toLowerCase();
        if (IDENTITY_HEADER_LC.has(lc)) return false;
        if (componentColumn && header === componentColumn) return false;
        return true;
      }),
    [headers, componentColumn],
  );

  return (
    <section
      className={styles.section}
      aria-labelledby="assignment-date-columns-heading"
    >
      <h3 id="assignment-date-columns-heading" className={styles.heading}>
        Assignment dates
      </h3>
      <p className={styles.hint}>
        Map CSV columns to the per-row start and end dates sent to Christmas.
        Defaults to <code>Start Date</code> / <code>End Date</code> when those
        headers are present. Leave blank to skip. Accepted cell formats:{" "}
        {ACCEPTED_ONBOARDING_DATE_FORMATS}.
      </p>
      <div className={styles.row}>
        <div className={styles.field}>
          <label htmlFor="start-date-column">Start date column</label>
          <select
            id="start-date-column"
            className={styles.select}
            value={startDateColumn}
            disabled={disabled}
            onChange={(e) => onStartDateColumnChange(e.target.value)}
          >
            <option value="">— none —</option>
            {dateHeaders.map((header) => (
              <option
                key={`start-${header}`}
                value={header}
                disabled={header === endDateColumn && header !== ""}
              >
                {header}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.field}>
          <label htmlFor="end-date-column">End date column</label>
          <select
            id="end-date-column"
            className={styles.select}
            value={endDateColumn}
            disabled={disabled}
            onChange={(e) => onEndDateColumnChange(e.target.value)}
          >
            <option value="">— none —</option>
            {dateHeaders.map((header) => (
              <option
                key={`end-${header}`}
                value={header}
                disabled={header === startDateColumn && header !== ""}
              >
                {header}
              </option>
            ))}
          </select>
        </div>
      </div>
    </section>
  );
}
